/**
 * ColonySummaryPanel — per-colony visual summary for the single-image
 * (colony-counting) analysis flow.
 *
 * Shows three views over the colonies returned by `/cpu/phenotypic/measure`:
 *   - HSV scatter: hue vs saturation, one point per colony, dot color = mean RGB.
 *   - Color distribution: per-channel (H, S, V) box-and-whisker.
 *   - Area scatter: sorted area, log-scale toggle.
 *
 * The backend gives mean Lab; we convert to HSV client-side (Lab → XYZ →
 * sRGB → HSV). Conversion is approximate but consistent across colonies,
 * which is what matters for clustering / outlier detection on the plot.
 *
 * Hover: mousing over a dot fires `onHoverObject(colony_id)` so the parent's
 * canvas mask layer can brighten that colony's outline.
 */
import React, {
  useState, useMemo, useCallback, useEffect, useRef,
} from 'react';
import {
  Box, Typography, Button, ToggleButton, ToggleButtonGroup,
  CircularProgress, FormControlLabel, Switch, Divider,
  IconButton, Tooltip, Menu, MenuItem,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import { normalizeColonyLab } from '../utils/colonyLab';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer,
} from 'recharts';

// Utility: rows array -> CSV blob; download via a transient <a>.
function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function rowsToCsv(rows) {
  if (!rows.length) return '';
  const cols = ['id', 'area', 'circularity', 'hue', 'sat', 'val', 'rgbCss'];
  const head = cols.join(',');
  const body = rows.map(r => cols.map(c => {
    const v = r[c];
    if (v == null) return '';
    if (typeof v === 'string') return `"${v.replace(/"/g, '""')}"`;
    if (typeof v === 'number') return Number.isFinite(v) ? v.toString() : '';
    return JSON.stringify(v);
  }).join(',')).join('\n');
  return head + '\n' + body + '\n';
}
// Snapshot the recharts <svg> inside `containerEl` to a PNG blob.
async function svgContainerToPng(containerEl) {
  const svg = containerEl?.querySelector('svg');
  if (!svg) throw new Error('No chart to capture');
  // Recharts SVG inherits styles from the page; we need a self-contained copy.
  const clone = svg.cloneNode(true);
  // Make sure background is non-transparent so the PNG is readable on light bg too.
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  const w = svg.clientWidth || parseInt(svg.getAttribute('width'), 10) || 600;
  const h = svg.clientHeight || parseInt(svg.getAttribute('height'), 10) || 400;
  clone.setAttribute('width', w);
  clone.setAttribute('height', h);
  const xml = new XMLSerializer().serializeToString(clone);
  const svgBlob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);
  try {
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve; img.onerror = reject; img.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = w * 2; canvas.height = h * 2;  // 2x for retina-ish output
    const ctx = canvas.getContext('2d');
    // Transparent background — leave the canvas unfilled. Caller wanted the
    // PNG to drop cleanly onto light or dark slide decks; the dark panel
    // background bakes in if we fill it.
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ── Lab → HSV conversion ───────────────────────────────────────────────────
// Standard CIELab (D65) → linear sRGB → sRGB → HSV. The Lab values from
// scikit-image's rgb2lab are in the standard range (L in [0,100],
// a/b roughly in [-128, 127]).
function labToRgb([L, a, b]) {
  // Lab → XYZ
  const fy = (L + 16) / 116;
  const fx = fy + a / 500;
  const fz = fy - b / 200;
  const eps = 0.008856;
  const kappa = 903.3;
  const f3 = (t) => {
    const t3 = t * t * t;
    return t3 > eps ? t3 : (116 * t - 16) / kappa;
  };
  // D65 reference white
  const Xn = 95.047, Yn = 100.000, Zn = 108.883;
  const X = Xn * f3(fx);
  const Y = L > kappa * eps ? Yn * Math.pow(fy, 3) : Yn * (L / kappa);
  const Z = Zn * f3(fz);
  // XYZ → linear sRGB (D65)
  let rl =  3.2404542 * X / 100 + -1.5371385 * Y / 100 + -0.4985314 * Z / 100;
  let gl = -0.9692660 * X / 100 +  1.8760108 * Y / 100 +  0.0415560 * Z / 100;
  let bl =  0.0556434 * X / 100 + -0.2040259 * Y / 100 +  1.0572252 * Z / 100;
  // Linear → gamma sRGB
  const enc = (c) => c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
  const r = Math.max(0, Math.min(1, enc(rl)));
  const g = Math.max(0, Math.min(1, enc(gl)));
  const bb = Math.max(0, Math.min(1, enc(bl)));
  return [Math.round(r * 255), Math.round(g * 255), Math.round(bb * 255)];
}

function rgbToHsv([r, g, b]) {
  const rN = r / 255, gN = g / 255, bN = b / 255;
  const max = Math.max(rN, gN, bN);
  const min = Math.min(rN, gN, bN);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rN) h = ((gN - bN) / d) % 6;
    else if (max === gN) h = (bN - rN) / d + 2;
    else h = (rN - gN) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : d / max;
  const v = max;
  return [h, s, v]; // h in [0,360], s/v in [0,1]
}

function rgbToCss([r, g, b]) {
  return `rgb(${r}, ${g}, ${b})`;
}

// Box stats for a numeric array — Q1/median/Q3/min/max/outliers.
function boxStats(values) {
  if (!values || !values.length) return null;
  const a = [...values].sort((x, y) => x - y);
  const n = a.length;
  const q = (p) => {
    const idx = (n - 1) * p;
    const lo = Math.floor(idx), hi = Math.ceil(idx);
    return lo === hi ? a[lo] : a[lo] + (a[hi] - a[lo]) * (idx - lo);
  };
  const q1 = q(0.25), median = q(0.5), q3 = q(0.75);
  const iqr = q3 - q1;
  const lo = q1 - 1.5 * iqr, hi = q3 + 1.5 * iqr;
  const inliers = a.filter(v => v >= lo && v <= hi);
  return {
    min: inliers[0] ?? a[0],
    max: inliers[inliers.length - 1] ?? a[n - 1],
    q1, median, q3,
    outliers: a.filter(v => v < lo || v > hi),
  };
}

const TABS = [
  { key: 'hsv', label: 'HSV scatter' },
  { key: 'dist', label: 'Color dist.' },
  { key: 'area', label: 'Area' },
];

const ColonySummaryPanel = ({
  cpuUrl,
  sessionId,
  frameIdx,
  s3Path,
  onHoverObject,
  // Bump to force a refresh after edits / re-detection (parent passes a counter).
  // Should reflect *colony set* changes (add/delete/detect), NOT every UI tick.
  refreshTick = 0,
  // Optional: number of colonies the parent currently has. When 0 we skip the
  // fetch — there's nothing to measure and the backend would just return [].
  colonyCount = null,
  // Optional supplier of inline masks for single-image colony-count mode where
  // masks live only in memory until save. Returns
  // [{obj_id, rle: {counts, size}}] — when omitted, the backend reads from S3.
  getInlineMasks = null,
  // Single-image save: when supplied, a Save button appears in the header and
  // calls this with { masks, colonies } so the parent owns where it persists.
  onSave = null,
  saving = false,
  // Restore mode: pre-loaded colonies (same shape as the backend response).
  // When provided, the panel skips its own fetch entirely and renders against
  // these. Used by mode='colony_count' analyses re-opened from the file
  // explorer — backend session is gone but the saved phenotypic JSON has
  // everything we need.
  staticColonies = null,
}) => {
  const [tab, setTab] = useState('hsv');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [colonies, setColonies] = useState([]);
  const [logArea, setLogArea] = useState(false);
  const [downloadAnchor, setDownloadAnchor] = useState(null);
  const abortRef = useRef(null);
  const chartContainerRef = useRef(null);

  // Restore mode: hydrate from pre-loaded data and skip every fetch.
  useEffect(() => {
    if (Array.isArray(staticColonies)) setColonies(staticColonies);
  }, [staticColonies]);

  const fetchColonies = useCallback(async () => {
    if (staticColonies) return; // restore mode owns the data
    if (!cpuUrl || !sessionId || frameIdx == null) return;
    if (colonyCount === 0) {
      // Nothing to measure yet — clear stale results and bail.
      setColonies([]);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctl = new AbortController();
    abortRef.current = ctl;
    setLoading(true);
    setError(null);
    try {
      // Inline masks short-circuit the backend's S3 read of
      // Analysis/{session}/rle_data/frame_*.json. Required in single-image
      // colony-count mode where the masks live only in memory.
      const inlineMasks = typeof getInlineMasks === 'function' ? getInlineMasks() : null;
      const resp = await fetch(`${cpuUrl}/phenotypic/measure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          frame_idx: frameIdx,
          s3_path: s3Path || '',
          ...(inlineMasks && inlineMasks.length > 0 ? { masks: inlineMasks } : {}),
        }),
        signal: ctl.signal,
      });
      if (!resp.ok) {
        const detail = await resp.json().catch(() => ({}));
        throw new Error(detail.detail || `Measure failed (${resp.status})`);
      }
      const data = await resp.json();
      setColonies(Array.isArray(data.colonies) ? data.colonies : []);
    } catch (e) {
      if (e.name === 'AbortError') return;
      setError(e.message || 'Failed to measure colonies');
    } finally {
      setLoading(false);
    }
  }, [cpuUrl, sessionId, frameIdx, s3Path, colonyCount, getInlineMasks, staticColonies]);

  // Auto-fetch when refreshTick changes — but only once per change, debounced
  // so a burst of edits coalesces into a single backend call. The parent
  // controls cadence via what it passes for refreshTick (we expect it to be
  // tied to colony-set membership, NOT every render).
  useEffect(() => {
    if (staticColonies) return; // pre-loaded; skip the auto-fetch
    if (!cpuUrl || !sessionId || frameIdx == null) return;
    if (colonyCount === 0) {
      setColonies([]);
      return;
    }
    const t = setTimeout(() => { fetchColonies(); }, 400);
    return () => {
      clearTimeout(t);
      if (abortRef.current) abortRef.current.abort();
    };
    // Intentionally do NOT include fetchColonies — its identity changes on
    // every prop change and would re-fire the timer. Refresh fires only on
    // the explicit refreshTick + the deps that materially change the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick, cpuUrl, sessionId, frameIdx, colonyCount, staticColonies]);

  // Enrich with HSV + RGB derived from Lab.
  const rows = useMemo(() => colonies.map(c => {
    const lab = normalizeColonyLab(c.mean_color_lab);
    const rgb = lab ? labToRgb(lab) : [128, 128, 128];
    const [h, s, v] = rgbToHsv(rgb);
    return {
      id: c.colony_id,
      area: c.area_pixels,
      circularity: c.circularity,
      hue: h,
      sat: s * 100,        // %
      val: v * 100,        // %
      rgbCss: rgbToCss(rgb),
    };
  }), [colonies]);

  // Per-channel distribution stats. Hook must run on every render (no early
  // returns above it) — rendered only by the 'dist' tab branch below.
  const dist = useMemo(() => {
    if (rows.length === 0) return null;
    return {
      hue: boxStats(rows.map(r => r.hue)),
      sat: boxStats(rows.map(r => r.sat)),
      val: boxStats(rows.map(r => r.val)),
    };
  }, [rows]);

  // Empty / loading / error states get rendered once at the top so the chart
  // panels can assume a non-empty rows array.
  if (loading && rows.length === 0) {
    return (
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
        <CircularProgress size={20} sx={{ mr: 1 }} />
        <Typography variant="caption" sx={{ color: '#aaa' }}>Measuring colonies…</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" sx={{ color: '#f44336' }}>{error}</Typography>
        <Button size="small" onClick={fetchColonies} sx={{ mt: 1 }}>Retry</Button>
      </Box>
    );
  }
  if (!loading && rows.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" sx={{ color: '#888' }}>
          No colonies measured yet. Detect or add colonies, then refresh.
        </Typography>
        <Button size="small" onClick={fetchColonies} sx={{ mt: 1, display: 'block' }}>
          Measure now
        </Button>
      </Box>
    );
  }

  const HoverDot = (props) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return null;
    return (
      <circle
        cx={cx} cy={cy} r={5}
        fill={payload.rgbCss}
        stroke="#fff" strokeWidth={1}
        onMouseEnter={() => onHoverObject && onHoverObject(payload.id)}
        onMouseLeave={() => onHoverObject && onHoverObject(null)}
        style={{ cursor: 'pointer' }}
      />
    );
  };

  const TooltipBody = ({ active, payload }) => {
    if (!active || !payload || !payload[0]) return null;
    const r = payload[0].payload;
    return (
      <Box sx={{ bgcolor: '#1e1e1e', border: '1px solid #444', p: 1, fontSize: '0.7rem', color: '#ddd' }}>
        <div>#{r.id}</div>
        <div>area {Math.round(r.area).toLocaleString()} px</div>
        <div>H {r.hue.toFixed(0)}° · S {r.sat.toFixed(0)}% · V {r.val.toFixed(0)}%</div>
      </Box>
    );
  };

  // Box-and-whisker built from `dist` above. Recharts doesn't ship a box plot
  // primitive, so each channel renders as a thin min-max line, a Q1-Q3 box,
  // and a tick for the median (see the 'dist' tab branch below).

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0,
      bgcolor: '#1c1c1e', color: '#fff', overflow: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{
        px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        bgcolor: '#111', borderBottom: '1px solid #333',
      }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Colony summary <Typography component="span" variant="caption" sx={{ color: '#888', ml: 1 }}>
            {rows.length} colonies
          </Typography>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Download">
            <span>
              <IconButton
                size="small"
                onClick={(e) => setDownloadAnchor(e.currentTarget)}
                disabled={rows.length === 0}
                sx={{ color: '#aaa' }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {onSave && (
            <Tooltip title="Save this analysis">
              <span>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<SaveIcon fontSize="small" />}
                  onClick={() => onSave({ rows, colonies })}
                  disabled={saving || rows.length === 0}
                  sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.25, px: 1 }}
                >
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </span>
            </Tooltip>
          )}
          <Button
            size="small"
            variant="outlined"
            onClick={fetchColonies}
            disabled={loading}
            sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.25, px: 1 }}
          >
            {loading ? '…' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      <Menu
        anchorEl={downloadAnchor}
        open={Boolean(downloadAnchor)}
        onClose={() => setDownloadAnchor(null)}
        PaperProps={{ sx: { bgcolor: '#1e1e1e', color: '#fff' } }}
      >
        <MenuItem
          onClick={() => {
            setDownloadAnchor(null);
            downloadBlob(
              `colonies_${sessionId || 'session'}_frame${frameIdx ?? 0}.csv`,
              new Blob([rowsToCsv(rows)], { type: 'text/csv;charset=utf-8' }),
            );
          }}
        >
          Raw data (CSV)
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDownloadAnchor(null);
            downloadBlob(
              `colonies_${sessionId || 'session'}_frame${frameIdx ?? 0}.json`,
              new Blob([JSON.stringify({ session_id: sessionId, frame_idx: frameIdx, colonies: rows }, null, 2)],
                { type: 'application/json' }),
            );
          }}
        >
          Raw data (JSON)
        </MenuItem>
        <MenuItem
          onClick={async () => {
            setDownloadAnchor(null);
            try {
              const blob = await svgContainerToPng(chartContainerRef.current);
              if (!blob) throw new Error('PNG export returned empty blob');
              downloadBlob(`colonies_${tab}_${sessionId || 'session'}.png`, blob);
            } catch (e) {
              // eslint-disable-next-line no-alert
              alert(`Chart export failed: ${e.message}`);
            }
          }}
        >
          Current chart (PNG)
        </MenuItem>
      </Menu>

      {/* Tabs */}
      <Box sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid #2a2a2a' }}>
        <ToggleButtonGroup
          value={tab}
          exclusive
          size="small"
          onChange={(_, v) => v && setTab(v)}
          sx={{
            '& .MuiToggleButton-root': {
              color: '#bbb', borderColor: '#444', py: 0.2, px: 1,
              fontSize: '0.72rem', textTransform: 'none',
            },
            '& .Mui-selected': { color: '#fff', bgcolor: '#1976d2 !important' },
          }}
        >
          {TABS.map(t => <ToggleButton key={t.key} value={t.key}>{t.label}</ToggleButton>)}
        </ToggleButtonGroup>
      </Box>

      {/* Chart */}
      <Box ref={chartContainerRef} sx={{ flex: 1, minHeight: 0, p: 1 }}>
        {tab === 'hsv' && (
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 12, bottom: 28, left: 8 }}>
              <CartesianGrid stroke="#333" />
              <XAxis
                type="number" dataKey="hue" name="Hue" unit="°" domain={[0, 360]}
                tick={{ fill: '#888', fontSize: 10 }}
                label={{ value: 'Hue (°)', position: 'insideBottom', offset: -16, fill: '#888', fontSize: 10 }}
              />
              <YAxis
                type="number" dataKey="sat" name="Saturation" unit="%" domain={[0, 100]}
                tick={{ fill: '#888', fontSize: 10 }}
                label={{ value: 'Saturation (%)', angle: -90, position: 'insideLeft', fill: '#888', fontSize: 10 }}
              />
              <RTooltip content={<TooltipBody />} cursor={{ stroke: '#444' }} />
              <Scatter data={rows} shape={<HoverDot />} />
            </ScatterChart>
          </ResponsiveContainer>
        )}

        {tab === 'dist' && dist && (
          <Box sx={{ p: 1 }}>
            {['hue', 'sat', 'val'].map((ch, i) => {
              const s = dist[ch];
              if (!s) return null;
              const axisMax = ch === 'hue' ? 360 : 100;
              const unit = ch === 'hue' ? '°' : '%';
              const label = ch === 'hue' ? 'Hue' : ch === 'sat' ? 'Saturation' : 'Value';
              const toPct = (v) => `${(v / axisMax) * 100}%`;
              return (
                <Box key={ch} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#ddd' }}>{label}</Typography>
                    <Typography variant="caption" sx={{ color: '#888', fontFamily: 'monospace' }}>
                      med {s.median.toFixed(1)}{unit} · IQR {(s.q3 - s.q1).toFixed(1)}{unit}
                    </Typography>
                  </Box>
                  {/* Track */}
                  <Box sx={{ position: 'relative', height: 22, bgcolor: '#2c2c2e', borderRadius: 0.5 }}>
                    {/* min–max whisker */}
                    <Box sx={{
                      position: 'absolute', top: '50%', height: 2, transform: 'translateY(-50%)',
                      bgcolor: '#666', left: toPct(s.min), width: toPct(s.max - s.min),
                    }} />
                    {/* Q1–Q3 box */}
                    <Box sx={{
                      position: 'absolute', top: 3, bottom: 3, bgcolor: '#1976d2', opacity: 0.7,
                      left: toPct(s.q1), width: toPct(s.q3 - s.q1), borderRadius: 0.5,
                    }} />
                    {/* Median tick */}
                    <Box sx={{
                      position: 'absolute', top: 2, bottom: 2, width: 2, bgcolor: '#fff',
                      left: toPct(s.median),
                    }} />
                    {/* Outliers */}
                    {s.outliers.slice(0, 30).map((o, j) => (
                      <Box key={j} sx={{
                        position: 'absolute', top: '50%', width: 4, height: 4,
                        bgcolor: '#ef5350', borderRadius: '50%',
                        left: toPct(o), transform: 'translate(-50%, -50%)',
                      }} />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: '#666', mt: 0.25 }}>
                    <span>0{unit}</span><span>{axisMax}{unit}</span>
                  </Box>
                  {i < 2 && <Divider sx={{ bgcolor: '#222', mt: 1.5 }} />}
                </Box>
              );
            })}
          </Box>
        )}

        {tab === 'area' && (
          <>
            <Box sx={{ px: 1, pb: 0.5 }}>
              <FormControlLabel
                control={<Switch size="small" checked={logArea} onChange={(e) => setLogArea(e.target.checked)} />}
                label={<Typography variant="caption">Log scale</Typography>}
                sx={{ color: '#aaa' }}
              />
            </Box>
            <ResponsiveContainer width="100%" height="92%">
              <ScatterChart margin={{ top: 10, right: 12, bottom: 28, left: 8 }}>
                <CartesianGrid stroke="#333" />
                <XAxis
                  type="number" dataKey="rank" name="Rank" allowDecimals={false}
                  tick={{ fill: '#888', fontSize: 10 }}
                  label={{ value: 'Colony rank (small → large)', position: 'insideBottom', offset: -16, fill: '#888', fontSize: 10 }}
                />
                <YAxis
                  type="number" dataKey="area" name="Area" unit=" px"
                  scale={logArea ? 'log' : 'linear'}
                  domain={logArea ? [1, 'dataMax'] : [0, 'dataMax']}
                  tick={{ fill: '#888', fontSize: 10 }}
                  label={{ value: 'Area (px)', angle: -90, position: 'insideLeft', fill: '#888', fontSize: 10 }}
                />
                <RTooltip content={<TooltipBody />} cursor={{ stroke: '#444' }} />
                <Scatter
                  data={[...rows].sort((a, b) => a.area - b.area).map((r, i) => ({ ...r, rank: i + 1 }))}
                  shape={<HoverDot />}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ColonySummaryPanel;
